'use client';

import { useState, useCallback, useEffect } from 'react';
import NotificationToast from './NotificationToast';

// Global window tipi tanımı
declare global {
  interface Window {
    showNotification?: {
      success: (message: string) => void;
      error: (message: string) => void;
      info: (message: string) => void;
      warning: (message: string) => void;
    };
  }
}

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

export default function NotificationManager() {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification = { ...notification, id };
    
    setNotifications(prev => [...prev, newNotification]);
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Global notification methods
  const showSuccess = useCallback((message: string, duration?: number) => {
    addNotification({ message, type: 'success', duration });
  }, [addNotification]);

  const showError = useCallback((message: string, duration?: number) => {
    addNotification({ message, type: 'error', duration });
  }, [addNotification]);

  const showInfo = useCallback((message: string, duration?: number) => {
    addNotification({ message, type: 'info', duration });
  }, [addNotification]);

  const showWarning = useCallback((message: string, duration?: number) => {
    addNotification({ message, type: 'warning', duration });
  }, [addNotification]);

  // Expose methods globally - useEffect içinde ayarla
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.showNotification = {
        success: showSuccess,
        error: showError,
        info: showInfo,
        warning: showWarning,
      };
    }
  }, [showSuccess, showError, showInfo, showWarning]);

  return (
    <div className="fixed top-4 right-4 left-4 sm:left-auto sm:right-4 z-50 space-y-2 pointer-events-none">
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          className="pointer-events-auto"
          style={{ 
            transform: `translateY(${index * 80}px)`,
            zIndex: 1000 - index 
          }}
        >
          <NotificationToast
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        </div>
      ))}
    </div>
  );
}
